import { ReactNode } from 'react';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'success' | 'danger';
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  className?: string;
  color?: string; // Custom color for background/text
  textColor?: string; // Custom text color
  hoverColor?: string; // Custom hover color
  focusRingColor?: string; // Custom focus ring color
  'data-testid'?: string;
}

const Button = ({
  variant = 'primary',
  type = 'button',
  children,
  onClick = () => undefined,
  disabled = false,
  fullWidth = false,
  isLoading = false,
  className = '',
  color,
  textColor,
  hoverColor,
  focusRingColor,
  'data-testid': dataTestId
}: ButtonProps) => {
  const baseStyle =
    'inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-gray-800 transition ease-in-out duration-150';

  const variants = {
    primary: 'bg-black text-white hover:bg-gray-800 focus:ring-gray-700 dark:bg-white dark:text-black dark:hover:bg-gray-100 dark:focus:ring-gray-300',
    secondary: 'bg-white text-black border border-gray-300 hover:bg-gray-100 focus:ring-gray-400 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-500',
    outline: 'border border-black text-black bg-white hover:bg-gray-50 focus:ring-gray-400 dark:border-white dark:text-white dark:bg-transparent dark:hover:bg-gray-800 dark:focus:ring-gray-500',
    text: 'text-black hover:text-gray-700 focus:ring-gray-300 dark:text-white dark:hover:text-gray-300 dark:focus:ring-gray-600',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-400 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-500',
    danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-400 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-500'
  };

  const disabledStyle = 'opacity-50 cursor-not-allowed dark:opacity-40';

  const widthStyle = fullWidth ? 'w-full' : '';

      // Generate custom color styles if provided (for Tailwind classes)
  const getCustomColorStyles = () => {
    if (!focusRingColor) {
      return '';
    }

    return `focus:ring-[${focusRingColor}]`;
  };

        // Generate inline styles for custom colors
  const getInlineStyles = () => {
    const styles: React.CSSProperties & { [key: string]: string } = {};

    if (color) {
      if (variant === 'outline') {
        styles.borderColor = color;
        styles.color = color;
      } else if (variant === 'text') {
        styles.color = color;
      } else {
        styles.backgroundColor = color;
      }
    }

    if (textColor) {
      styles.color = textColor;
    }

    // Add hover styles using CSS custom properties
    if (hoverColor) {
      if (variant === 'outline') {
        styles['--hover-bg-color'] = hoverColor;
        styles['--hover-border-color'] = hoverColor;
      } else if (variant === 'text') {
        styles['--hover-text-color'] = hoverColor;
      } else {
        styles['--hover-bg-color'] = hoverColor;
      }
    }

    return styles;
  };

  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={dataTestId}
      disabled={disabled || isLoading}
      className={`${baseStyle} ${variants[variant]} ${getCustomColorStyles()} ${disabled || isLoading ? disabledStyle : ''} ${widthStyle} ${className}`}
      style={getInlineStyles()}
    >
      {isLoading ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true">Please Wait...</span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
